/**
 * Hero geometry: layered orbital trajectories, a technical grid and points of
 * light travelling through the system. Decorative; reacts to --px/--py.
 */
export function OrbitField() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      <div
        className="absolute -top-1/4 left-[58%] h-[110vh] w-[110vw] -translate-x-1/2 opacity-80"
        style={{
          background:
            "radial-gradient(closest-side, color-mix(in oklab, var(--primary) 20%, transparent), transparent 70%)",
          transform:
            "translate3d(calc(-50% + var(--px, 0) * 22px), calc(var(--py, 0) * 16px), 0)",
          transition: "transform 900ms cubic-bezier(0.16,1,0.3,1)",
          animation: "bmt-drift 26s ease-in-out infinite",
        }}
      />

      <div
        className="absolute inset-0 grid-field opacity-60"
        style={{
          maskImage: "radial-gradient(78% 64% at 55% 46%, black, transparent)",
          transform: "translate3d(calc(var(--px, 0) * -8px), calc(var(--py, 0) * -6px), 0)",
          transition: "transform 1200ms cubic-bezier(0.16,1,0.3,1)",
        }}
      />

      {/* Far layer */}
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        fill="none"
        style={{
          transform: "translate3d(calc(var(--px, 0) * 10px), calc(var(--py, 0) * 8px), 0)",
          transition: "transform 1400ms cubic-bezier(0.16,1,0.3,1)",
        }}
      >
        <defs>
          <linearGradient id="bmt-far" x1="0" y1="1" x2="1" y2="0">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0" />
            <stop offset="70%" stopColor="var(--accent)" stopOpacity="0.22" />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d="M -160 900 C 420 660, 980 520, 1620 560" stroke="url(#bmt-far)" strokeWidth="1" />
        <path d="M -160 980 C 520 760, 1060 640, 1640 700" stroke="url(#bmt-far)" strokeWidth="1" />
        <g fill="color-mix(in oklab, var(--foreground) 45%, transparent)">
          <circle cx="240" cy="742" r="1.6" style={{ animation: "bmt-twinkle 7s ease-in-out infinite" }} />
          <circle cx="1044" cy="566" r="1.4" style={{ animation: "bmt-twinkle 9s 1.2s ease-in-out infinite" }} />
          <circle cx="640" cy="648" r="1.2" style={{ animation: "bmt-twinkle 11s .6s ease-in-out infinite" }} />
        </g>
      </svg>

      {/* Near layer: primary trajectory + travelling light */}
      <svg
        className="absolute inset-0 h-full w-full"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        fill="none"
        style={{
          transform: "translate3d(calc(var(--px, 0) * -20px), calc(var(--py, 0) * -14px), 0)",
          transition: "transform 1100ms cubic-bezier(0.16,1,0.3,1)",
        }}
      >
        <defs>
          <linearGradient id="bmt-near" x1="0" y1="1" x2="1" y2="0">
            <stop offset="0%" stopColor="var(--primary)" stopOpacity="0" />
            <stop offset="42%" stopColor="var(--primary)" stopOpacity="0.7" />
            <stop offset="100%" stopColor="white" stopOpacity="0.85" />
          </linearGradient>
          <linearGradient id="bmt-spark" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="white" stopOpacity="0" />
            <stop offset="100%" stopColor="white" stopOpacity="0.9" />
          </linearGradient>
        </defs>

        <path
          id="bmt-path-main"
          d="M -140 760 C 360 470, 900 268, 1560 306"
          stroke="url(#bmt-near)"
          strokeWidth="1.25"
          strokeDasharray="2400"
          style={{ animation: "bmt-arc-draw 3s cubic-bezier(0.16,1,0.3,1) forwards" }}
        />
        <path
          d="M -140 760 C 360 470, 900 268, 1560 306"
          stroke="url(#bmt-spark)"
          strokeWidth="1.75"
          strokeDasharray="120 2280"
          style={{ animation: "bmt-travel 9s 3s linear infinite" }}
        />

        {/* Intersecting architectural lines */}
        <g stroke="color-mix(in oklab, var(--foreground) 12%, transparent)" strokeWidth="1">
          <path d="M 1088 0 L 1088 900" />
          <path d="M 0 306 L 1440 306" strokeDasharray="2 8" />
        </g>

        {/* Node at the intersection */}
        <g style={{ animation: "bmt-orbit-float 14s ease-in-out infinite" }}>
          <circle cx="1088" cy="306" r="34" fill="white" opacity="0.05" />
          <circle cx="1088" cy="306" r="14" fill="none" stroke="color-mix(in oklab, var(--primary) 70%, transparent)" strokeWidth="1" />
          <circle cx="1088" cy="306" r="2.6" fill="white" />
          <circle cx="1088" cy="306" r="9" fill="var(--primary)" style={{ animation: "bmt-node-pulse 5s ease-in-out infinite" }} />
        </g>
      </svg>

      <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-b from-transparent to-background" />
    </div>
  );
}
